<template lang="">
  <div v-if="loggedIn">
    {{ errorContent }}
    <b-row class="match-height">
      <b-col
        xl="3"
        md="4"
        sm="6"
      >
        <statistic-card-vertical
          color="warning"
          icon="UsersIcon"
          :statistic="customerCount"
          statistic-title="Kunden"
        />
      </b-col>
      <b-col
        xl="3"
        md="4"
        sm="6"
      >
        <statistic-card-vertical
          color="warning"
          icon="CodesandboxIcon"
          :statistic="organizationCustomersCount"
          statistic-title="Gewerbekunden"
        />
      </b-col>
      <b-col
        v-if="newestCustomer"
        xl="3"
        md="4"
        sm="6"
      >
        <a :href="`/customers-view/${newestCustomer.id}`">
          <feather-icon
            style="position: absolute; z-index: 10; right: 20; top: 5"
            icon="ExternalLinkIcon"
            size="20"
          />
          <statistic-card-vertical
            color="primary"
            icon="UserPlusIcon"
            :statistic="
              newestCustomer.firstName + ' ' + newestCustomer.lastName
            "
            statistic-title="Neuster Kunde"
          />
        </a>
      </b-col>
      <b-col
        v-if="customerWithMostOrders"
        xl="3"
        md="4"
        sm="6"
      >
        <a :href="`/customers-view/${customerWithMostOrders.id}`">
          <feather-icon
            style="position: absolute; z-index: 10; right: 20; top: 5"
            icon="ExternalLinkIcon"
            size="20"
          />
          <statistic-card-vertical
            icon="ShoppingCartIcon"
            :statistic="customerWithMostOrdersDisplay"
            statistic-title="Meiste Buchungen"
            color="info"
          />
        </a>
      </b-col>
    </b-row>
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mr-1">Zeige</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-1"
              style="min-width: 80px; max-width: 100px;"
            />
            <label class="ml-1">Einträge</label>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Suche..."
                type="search"
              />
              <b-button
                variant="primary"
                @click="createNewObject()"
              >
                <span class="text-nowrap">Kunde hinzufügen</span>
              </b-button>
              <b-sidebar
                id="sidebar-right"
                bg-variant="white"
                right
                backdrop
                shadow
                no-header
              >
                <sidebar-content />
              </b-sidebar>
              <b-sidebar
                id="sidebar-right-edit"
                bg-variant="white"
                right
                backdrop
                shadow
                no-header
              >
                <sidebar-content
                  :id="editCustomerData.id"
                  :is-edit-mode="isEditMode"
                  :edit-customer-entry="editCustomerData"
                />
              </b-sidebar>
            </div>
          </b-col>
        </b-row>
      </div>
      <div>
        <b-card no-body>
          <!-- Table -->
          <b-table
            ref="refListTable"
            striped
            hover
            :busy="loading"
            :items="customerEntries"
            :fields="fields"
            :filter="filter"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            show-empty
            empty-text="Keine Einträge gefunden"
            @row-clicked="onRowClicked"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-2" />
                <strong>Lade Kundendaten...</strong>
              </div>
            </template>
            <template #cell(firstName)="data">
              {{ data.item.firstName }} {{ data.item.lastName }}
            </template>

            <template #cell(orders.length)="data">
              <b-badge variant="success">
                {{ data.value }}
              </b-badge>
            </template>

            <!-- Column: Actions -->
            <template #cell(actions)="data">
              <b-dropdown
                variant="link"
                no-caret
              >
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>
                <b-dropdown-item
                  :to="{ name: 'customers-view', params: { id: data.item.id } }"
                >
                  <feather-icon icon="FileTextIcon" />
                  <span class="align-middle ml-50">Öffnen</span>
                </b-dropdown-item>

                <b-dropdown-item @click="editObject(data)">
                  <feather-icon icon="EditIcon" />
                  <span class="align-middle ml-50">Bearbeiten</span>
                </b-dropdown-item>

                <b-dropdown-item @click="deleteWarning(data)">
                  <feather-icon icon="TrashIcon" />
                  <span class="align-middle ml-50">Löschen</span>
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span
                  class="text-muted"
                >Zeige {{ dataMeta.from }} bis {{ dataMeta.to }} von
                  {{ dataMeta.of }} Einträge</span>
              </b-col>

              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="currentPage"
                  :total-rows="getTotalRows"
                  :per-page="perPage"
                  first-number
                  last-number
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mb-0"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </div>
    </b-card>
  </div>
  <div v-else>
    Forbidden
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BPagination,
  BBadge,
  BLink,
  BSidebar,
  BSpinner,
  VBToggle,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import customerMetrics from '@/helper/customerMetrics'
import tableHelper from '../../Table+Helper'
import SidebarContent from './CustomerListAddNew.vue'

export default {
  setup() {
    const {
      perPageOptions,
      perPage,
      filter,
      totalRows,
      currentPage,
      refListTable,
      dataMeta,
      refetchData,
    } = tableHelper()

    const {
      getCustomerCount,
      getOrganizationCustomersCount,
      getNewestCustomer,
      getCustomerWithMostOrders,
      getTopCustomersByRevenue,
      getActiveCustomers,
      getCustomerRetentionRate,
      getAverageCustomerValue,
    } = customerMetrics()

    return {
      perPageOptions,
      filter,
      perPage,
      totalRows,
      currentPage,
      refListTable,
      dataMeta,
      refetchData,
      // Customer metrics
      getCustomerCount,
      getOrganizationCustomersCount,
      getNewestCustomer,
      getCustomerWithMostOrders,
      getTopCustomersByRevenue,
      getActiveCustomers,
      getCustomerRetentionRate,
      getAverageCustomerValue,
    }
  },
  components: {
    BTable,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BPagination,
    BBadge,
    BLink,
    BSpinner,
    vSelect,
    StatisticCardVertical,
    BSidebar,
    VBToggle,
    SidebarContent,
    BDropdown,
    BDropdownItem,
    ToastificationContent,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  data() {
    return {
      errorContent: '',
      editCustomerData: {},
      isEditMode: false,
      loading: false,
      fields: [
        {
          key: 'id',
          label: 'KDNR',
          sortable: true,
        },
        {
          key: 'firstName',
          label: 'Kunde',
          sortable: true,
        },
        {
          key: 'organizationName',
          label: 'Gewerbe',
          sortable: true,
        },
        {
          key: 'email',
          label: 'E-Mail',
          sortable: true,
        },
        {
          key: 'city',
          label: 'Stadt',
          sortable: true,
        },
        {
          key: 'orders.length',
          label: 'Buchungen',
          sortable: true,
        },
        {
          key: 'actions',
          label: 'Aktionen',
        },
      ],
    }
  },
  computed: {
    customerEntries() {
      const customers = this.$store.getters.customerEntries || []
      return [...customers].sort((a, b) => {
        const dateA = new Date(a.created_at)
        const dateB = new Date(b.created_at)
        return dateB.getTime() - dateA.getTime() // Newest first
      })
    },
    loggedIn() {
      return this.$store.state.authentication.status.loggedIn
    },
    getTotalRows() {
      return this.customerEntries.length
    },
    customerCount() {
      return this.getCustomerCount()
    },
    organizationCustomersCount() {
      return this.getOrganizationCustomersCount()
    },
    newestCustomer() {
      return this.getNewestCustomer()
    },
    customerWithMostOrders() {
      return this.getCustomerWithMostOrders()
    },
    customerWithMostOrdersDisplay() {
      if (!this.customerWithMostOrders) return ''
      const ordersCount = this.customerWithMostOrders.orders?.length || 0
      return `${this.customerWithMostOrders.firstName} ${this.customerWithMostOrders.lastName} (${ordersCount})`
    },
  },
  async mounted() {
    await this.loadCustomers()
  },
  updated() {
    // Set the initial number of items
    this.totalRows = this.getTotalRows
  },
  methods: {
    async loadCustomers() {
      this.loading = true
      this.errorContent = ''
      try {
        await this.$store.dispatch('getCustomerEntries')
      } catch (error) {
        console.error('Error loading customers:', error)
        this.errorContent = 'Fehler beim Laden der Kundendaten. Bitte versuchen Sie es erneut.'
        this.toastNotification(
          'Kundendaten konnten nicht geladen werden.',
          'AlertTriangleIcon',
          'danger'
        )
      } finally {
        this.loading = false
      }
    },
    onRowClicked(item) {
      this.$router.push(`/customers-view/${item.id}`)
    },
    deleteWarning(data) {
      this.$swal({
        title: 'Bist du sicher?',
        // text: 'Das Lager wird unwiederruflich gelöscht.',
        text: `Den Kunden "${data.item.firstName} ${data.item.lastName}" wirklich löschen?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-primary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          this.loading = true
          try {
            await this.$store.dispatch('deleteCustomer', data.item.id)
            await this.loadCustomers()
            this.toastNotification(
              'Kunde erfolgreich gelöscht.',
              'CheckIcon',
              'success'
            )
          } catch (error) {
            console.error('Error deleting customer:', error)
            this.toastNotification(
              'Kunde konnte nicht gelöscht werden.',
              'AlertTriangleIcon',
              'danger'
            )
          } finally {
            this.loading = false
          }
        }
      })
    },

    toastNotification(title, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },

    createNewObject() {
      this.isEditMode = false
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    editObject(data) {
      this.editCustomerData = data.item
      this.isEditMode = true
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-edit')
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
